import { useState } from 'react';
import { GlassWater } from 'lucide-react';
import { addMinutes, diagramIsEmpty, type Diagram, type SessionPlayerView } from '@hoopify/shared';
import { DiagramView } from '../diagram/DiagramView';
import { formatTime } from '../../lib/dates';

/** The plan as players and parents see it: times, drills, groups. Nothing coach-only. */
export function PlayerPlan({ plan, startTime }: { plan: SessionPlayerView['plan']; startTime: string }) {
  let offset = 0;
  if (!plan.sections.length) return <p className="text-muted">The coach hasn’t added any drills yet.</p>;
  return (
    <ol className="space-y-6">
      {plan.sections.map((s, si) => (
        <li key={si}>
          <h3 className="border-b border-line-strong pb-1.5 text-[16px] font-semibold">{s.title}</h3>
          <ol>
            {s.blocks.map((b, bi) => {
              const at = addMinutes(startTime, offset);
              offset += b.minutes;
              return (
                <li key={bi} className="grid grid-cols-[72px_minmax(0,1fr)] gap-3 border-b border-line py-2.5 last:border-b-0">
                  <span className="tnum text-[14px] font-semibold">{formatTime(at)}</span>
                  <div className="space-y-1">
                    {b.items.map((i, ii) => (
                      <div key={ii}>
                        <p className="flex flex-wrap items-baseline justify-between gap-x-3">
                          <span>
                            {b.items.length > 1 && i.group && <span className="mr-1.5 text-[13px] font-semibold text-action">{i.group}:</span>}
                            {i.title}
                          </span>
                          <span className="tnum text-[13px] text-muted">{i.minutes} min</span>
                        </p>
                        {i.diagram && !diagramIsEmpty(i.diagram) && <DiagramToggle diagram={i.diagram} />}
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>
          {!!s.breakAfter && (() => {
            const at = addMinutes(startTime, offset);
            offset += s.breakAfter;
            return (
              <p className="mt-3 grid grid-cols-[72px_minmax(0,1fr)] gap-3 rounded-md bg-sky-50 px-2 py-2 text-[14px] dark:bg-sky-950/30">
                <span className="tnum font-semibold">{formatTime(at)}</span>
                <span className="flex items-center gap-2">
                  <GlassWater className="size-4 text-sky-600 dark:text-sky-300" aria-hidden /> Drink break, {s.breakAfter} min
                </span>
              </p>
            );
          })()}
        </li>
      ))}
    </ol>
  );
}

function DiagramToggle({ diagram }: { diagram: Diagram }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1">
      <button className="text-[13px] font-medium text-action hover:underline" onClick={() => setOpen(!open)} aria-expanded={open}>
        {open ? 'Hide diagram' : 'Show diagram'}
      </button>
      {open && (
        <div className="mt-2">
          <DiagramView diagram={diagram} />
        </div>
      )}
    </div>
  );
}
